import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { FormFieldLabel } from "../FormFieldLabel";
import { useAuth } from "../../providers/AuthProvider";
import {
  listBarangays,
  listMunicipalities,
  listProvinces,
  listRegions,
  type GeoBarangay,
  type GeoMunicipality,
  type GeoProvince,
  type GeoRegion,
} from "../../features/shared/api/geo";
import { createLogger } from "../../utils/logger";

const logger = createLogger("GeoLocationSelect");

export type GeoSelection = {
  regionId?: string;
  provinceId?: string;
  municipalityId?: string;
  barangayId?: string;
};

type GeoLocationSelectProps = {
  value: GeoSelection;
  onChange: (value: GeoSelection) => void;
  disabled?: boolean;
  required?: boolean;
  includeBarangay?: boolean;
};

export function GeoLocationSelect({
  value,
  onChange,
  disabled = false,
  required = false,
  includeBarangay = false,
}: GeoLocationSelectProps) {
  const { t } = useTranslation();
  const { accessToken } = useAuth();

  const [regions, setRegions] = useState<GeoRegion[]>([]);
  const [provinces, setProvinces] = useState<GeoProvince[]>([]);
  const [municipalities, setMunicipalities] = useState<GeoMunicipality[]>([]);
  const [barangays, setBarangays] = useState<GeoBarangay[]>([]);

  const [regionsLoading, setRegionsLoading] = useState(false);
  const [provincesLoading, setProvincesLoading] = useState(false);
  const [municipalitiesLoading, setMunicipalitiesLoading] = useState(false);
  const [barangaysLoading, setBarangaysLoading] = useState(false);

  const [provinceCache, setProvinceCache] = useState<Record<string, GeoProvince[]>>({});
  const [municipalityCache, setMunicipalityCache] = useState<Record<string, GeoMunicipality[]>>({});
  const [barangayCache, setBarangayCache] = useState<Record<string, GeoBarangay[]>>({});

  useEffect(() => {
    if (!accessToken) {
      return;
    }
    let isActive = true;
    setRegionsLoading(true);
    listRegions(accessToken)
      .then((data) => {
        if (isActive) {
          setRegions(data);
        }
      })
      .catch((err) => {
        logger.warn("Failed to load regions", err);
      })
      .finally(() => {
        if (isActive) {
          setRegionsLoading(false);
        }
      });
    return () => {
      isActive = false;
    };
  }, [accessToken]);

  const cachedProvinces = value.regionId ? provinceCache[value.regionId] : undefined;
  const cachedMunicipalities = value.provinceId ? municipalityCache[value.provinceId] : undefined;
  const cachedBarangays = value.municipalityId ? barangayCache[value.municipalityId] : undefined;

  useEffect(() => {
    if (!accessToken || !value.regionId) {
      setProvinces([]);
      return;
    }
    if (cachedProvinces) {
      setProvinces(cachedProvinces);
      return;
    }
    let isActive = true;
    setProvincesLoading(true);
    listProvinces(accessToken, value.regionId)
      .then((data) => {
        if (isActive) {
          setProvinceCache((prev) => ({ ...prev, [value.regionId as string]: data }));
          setProvinces(data);
        }
      })
      .catch((err) => {
        logger.warn("Failed to load provinces", err);
      })
      .finally(() => {
        if (isActive) {
          setProvincesLoading(false);
        }
      });
    return () => {
      isActive = false;
    };
  }, [accessToken, value.regionId, cachedProvinces]);

  useEffect(() => {
    if (!accessToken || !value.provinceId) {
      setMunicipalities([]);
      if (includeBarangay) {
        setBarangays([]);
      }
      return;
    }
    if (cachedMunicipalities) {
      setMunicipalities(cachedMunicipalities);
      return;
    }
    let isActive = true;
    setMunicipalitiesLoading(true);
    listMunicipalities(accessToken, value.provinceId)
      .then((data) => {
        if (isActive) {
          setMunicipalityCache((prev) => ({ ...prev, [value.provinceId as string]: data }));
          setMunicipalities(data);
        }
      })
      .catch((err) => {
        logger.warn("Failed to load municipalities", err);
      })
      .finally(() => {
        if (isActive) {
          setMunicipalitiesLoading(false);
        }
      });
    return () => {
      isActive = false;
    };
  }, [accessToken, value.provinceId, cachedMunicipalities, includeBarangay]);

  useEffect(() => {
    if (!includeBarangay) {
      return;
    }
    if (!accessToken || !value.municipalityId) {
      setBarangays([]);
      return;
    }
    if (cachedBarangays) {
      setBarangays(cachedBarangays);
      return;
    }
    let isActive = true;
    setBarangaysLoading(true);
    listBarangays(accessToken, value.municipalityId)
      .then((data) => {
        if (isActive) {
          setBarangayCache((prev) => ({ ...prev, [value.municipalityId as string]: data }));
          setBarangays(data);
        }
      })
      .catch((err) => {
        logger.warn("Failed to load barangays", err);
      })
      .finally(() => {
        if (isActive) {
          setBarangaysLoading(false);
        }
      });
    return () => {
      isActive = false;
    };
  }, [accessToken, value.municipalityId, cachedBarangays, includeBarangay]);

  const regionOptions = useMemo(() => {
    const items = regions.slice().sort((a, b) => a.name.localeCompare(b.name));
    return items;
  }, [regions]);

  const provinceOptions = useMemo(() => {
    const items = provinces.slice().sort((a, b) => a.name.localeCompare(b.name));
    return items;
  }, [provinces]);

  const municipalityOptions = useMemo(() => {
    const items = municipalities.slice().sort((a, b) => a.name.localeCompare(b.name));
    return items;
  }, [municipalities]);
  const barangayOptions = useMemo(() => {
    if (!includeBarangay) {
      return [];
    }
    const items = barangays.slice().sort((a, b) => a.name.localeCompare(b.name));
    return items;
  }, [barangays, includeBarangay]);

  const handleRegionChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const regionId = event.target.value || undefined;
    onChange({
      regionId,
      provinceId: undefined,
      municipalityId: undefined,
    });
  };

  const handleProvinceChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const provinceId = event.target.value || undefined;
    onChange({
      ...value,
      provinceId,
      municipalityId: undefined,
    });
  };

  const handleMunicipalityChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const municipalityId = event.target.value || undefined;
    onChange({
      ...value,
      municipalityId,
      barangayId: undefined,
    });
  };

  const handleBarangayChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const barangayId = event.target.value || undefined;
    onChange({
      ...value,
      barangayId,
    });
  };

  const labels = {
    region: t("geo.region_label", "Region"),
    province: t("geo.province_label", "Province"),
    municipality: t("geo.municipality_label", "Municipality / City"),
    barangay: t("geo.barangay_label", "Barangay"),
  };

  const placeholders = {
    region: regionsLoading ? t("geo.loading_regions", "Loading regions...") : t("geo.select_region", "Select a region"),
    province:
      provincesLoading ? t("geo.loading_provinces", "Loading provinces...") : t("geo.select_province", "Select a province"),
    municipality:
      municipalitiesLoading
        ? t("geo.loading_municipalities", "Loading municipalities...")
        : t("geo.select_municipality", "Select a municipality"),
    barangay:
      barangaysLoading ? t("geo.loading_barangays", "Loading barangays...") : t("geo.select_barangay", "Select a barangay"),
  };

  const selectClasses =
    "block w-full rounded-xl border-0 py-2.5 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 disabled:bg-slate-50 disabled:text-slate-500 disabled:ring-slate-200 dark:text-slate-100 dark:bg-slate-900 dark:ring-slate-700 dark:disabled:bg-slate-800 dark:disabled:text-slate-500";
  const fieldClasses = "flex flex-col gap-2";

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <label className={fieldClasses}>
        <FormFieldLabel label={labels.region} optional={!required} />
        <select
          className={selectClasses}
          value={value.regionId ?? ""}
          onChange={handleRegionChange}
          disabled={disabled || regionsLoading}
          required={required}
        >
          <option value="">{placeholders.region}</option>
          {regionOptions.map((region) => (
            <option key={region.id} value={region.id}>
              {region.name}
            </option>
          ))}
        </select>
      </label>
      <label className={fieldClasses}>
        <FormFieldLabel label={labels.province} optional={!required} />
        <select
          className={selectClasses}
          value={value.provinceId ?? ""}
          onChange={handleProvinceChange}
          disabled={disabled || !value.regionId || provincesLoading}
          required={required}
        >
          <option value="">{placeholders.province}</option>
          {provinceOptions.map((province) => (
            <option key={province.id} value={province.id}>
              {province.name}
            </option>
          ))}
        </select>
      </label>
      <label className={fieldClasses}>
        <FormFieldLabel label={labels.municipality} optional={!required} />
        <select
          className={selectClasses}
          value={value.municipalityId ?? ""}
          onChange={handleMunicipalityChange}
          disabled={disabled || !value.provinceId || municipalitiesLoading}
          required={required}
        >
          <option value="">{placeholders.municipality}</option>
          {municipalityOptions.map((municipality) => (
            <option key={municipality.id} value={municipality.id}>
              {municipality.name}
            </option>
          ))}
        </select>
      </label>
      {includeBarangay && (
        <label className={fieldClasses}>
          <FormFieldLabel label={labels.barangay} optional={!required} />
          <select
            className={selectClasses}
            value={value.barangayId ?? ""}
            onChange={handleBarangayChange}
            disabled={disabled || !value.municipalityId || barangaysLoading}
          >
            <option value="">{placeholders.barangay}</option>
            {barangayOptions.map((barangay) => (
              <option key={barangay.id} value={barangay.id}>
                {barangay.name}
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
}
